<template>
  <v-autocomplete
    v-model="selectedContract"
    :label="$t('contract')"
    :data-testid="dataTestId"
    :id="elementId"
    :items="filteredContracts"
    :filter="filterItems"
    :no-data-text="$t('noOpenContracts')"
    :disabled="disabled"
    :hint="hint"
    color="black"
    item-value="contractId"
    return-object
  >
    <template #append-outer>
      <v-icon @click="refreshContracts(false)" color="primary" tabindex="-1">mdi-refresh</v-icon>
    </template>
    <template #item="{item}">
      <v-container>
        <v-row dense no-gutters>
          <v-col cols="6" v-for="(field, i) in contractFields" :key="i">
            <Icon
              :dataTestId="`contract-auto-${field.name}`"
              :icon="field.icon"
              :tooltipText="$t(field.name)"
              iconColor="grey"
              propagateClick
            />
            <span>
              {{item[field.name] ?? 'N/A'}}
            </span>
          </v-col>
        </v-row>
        <v-row>
          <v-divider/>
        </v-row>
      </v-container>
    </template>
    <template #selection="{item}">
      <div class="mr-2" v-for="(field, i) in contractFields" :key="i">
        <v-icon small class="mb-1 mr-1">{{field.icon}}</v-icon>
        <span>{{item[field.name] ?? 'N/A'}}</span>
      </div>
    </template>
  </v-autocomplete>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ContractStatus, ContractApprovalStatus, ContractMode } from '@/utils/Enumerations.js'
export default {
  name: 'ContractAutocomplete',

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    contractId: { type: Number },
    tractId: { type: Number },
    disabled: { type: Boolean, default: false },
    dataTestId: { type: String, default: 'contract-complete' },
    hint: { type: String, default: '' },
    contractMode: { type: Object, default: () => ({ value: 0 }) },
    includePaused: { type: Boolean, default: true },
    includeDraft: { type: Boolean, default: true },
    includeActiveApprovals: { type: Boolean, default: true },
    validStatuses: { type: Array, default: () => [ContractStatus.Open.value] },
    contractType: { type: Number, default: undefined },
    fetchContracts: { type: Boolean, default: true },
    elementId: { type: String, required: false, default: undefined }
  },

  data: () => ({
    contracts: [],
    selectedContract: undefined,
    closedInitialContract: undefined
  }),

  watch: {
    selectedContract (val) {
      if (val === undefined || val === null) {
        this.$emit('contract-chosen', undefined)
        return
      }

      this.$emit('contract-chosen', val)
    }
  },

  computed: {
    ...mapGetters('contract', ['allContracts']),

    contractsForAutocomplete () {
      const contracts = (this.contracts.length > 0) ? this.contracts : this.allContracts
      return this.closedInitialContract ? [this.closedInitialContract, ...contracts] : contracts
    },

    filteredContracts () {
      const contractClause = (c) => {
        const allowedStatus = this.validStatuses.includes(c.status)
        const includeDraft = this.includeDraft || !c.isDraft
        const includePaused = this.includePaused || !c.paused
        const includeActiveApprovals = this.includeActiveApprovals ||
          (c.approvalStatus === ContractApprovalStatus.Approved.value || c.approvalStatus === null)

        const isPropContract = c.contractId === this.contractId
        const handleSameType = this.contractMode === ContractMode.Byproducts && this.contractType !== undefined
          ? (c.type === this.contractType)
          : true

        return (allowedStatus && includeDraft && includePaused && includeActiveApprovals && handleSameType) || isPropContract
      }

      return this.contractsForAutocomplete.filter(contractClause)
    },

    contractFields () {
      return (this.contractMode === ContractMode.Logs)
        ? [
          { name: 'tract', icon: 'mdi-crosshairs-gps' },
          { name: 'destination', icon: 'mdi-domain' },
          { name: 'setting', icon: 'mdi-map-marker-circle' },
          { name: 'account', icon: 'mdi-account' }
        ]
        : [
          { name: 'fromAccount', icon: 'mdi-account-arrow-right' },
          { name: 'account', icon: 'mdi-account' },
          { name: 'destination', icon: 'mdi-domain' }
          // TODO: IH - Restore after reintroducting TSBP
          // { name: 'tract', icon: 'mdi-crosshairs-gps' }
        ]
    }
  },

  async created () {
    if (this.fetchContracts) {
      this.refreshContracts(true)
    } else {
      await this.setInitialContract()
    }
  },

  methods: {
    ...mapActions('contract', ['fetchContractsForAutocomplete', 'getContract']),
    ...mapActions('tract', ['fetchContractsForTract']),

    async refreshContracts (firstFetch) {
      this.contracts = this.tractId === undefined
        ? await this.fetchContractsForAutocomplete({
          includeOpen: true,
          includeOnHold: false,
          includeClosed: false,
          includeExpired: false,
          limitToDraft: false,
          limitToInternal: false,
          limitToPaused: false,
          includeProduction: this.contractMode === ContractMode.Logs,
          includeWoodsSale: this.contractMode === ContractMode.Logs,
          includeByProduct: this.contractMode === ContractMode.Byproducts,
          includeByproductPurchase: this.contractMode === ContractMode.Byproducts,
          includeByproductHauling: this.contractMode === ContractMode.Byproducts,
          includeTransfer: this.contractMode === ContractMode.Transfers,
          includeLogYardSale: this.contractMode === ContractMode.LogYardSale
        })
        : await this.fetchContractsForTract(this.tractId)

      if (firstFetch) {
        this.setInitialContract()
      }
    },

    filterItems (item, queryText) {
      const search = queryText.toLowerCase()
      return this.contractFields.some(cf => (item?.[cf.name] ?? '').toLowerCase().indexOf(search) > -1)
    },

    async setInitialContract () {
      this.selectedContract = (this.contractId)
        ? this.allContracts.find(c => c.contractId === this.contractId)
        : (this.filteredContracts.length === 1)
          ? this.filteredContracts[0]
          : undefined

      if (this.selectedContract === undefined && this.contractId) {
        const contract = await this.getContract(this.contractId)
        this.closedInitialContract = contract
        this.selectedContract = contract
      }
    }
  }
}
</script>

<style scoped>
.v-select__selection,
.v-select__selection--comma,
.v-select.v-text-field input {
  color: blue !important;
}
</style>
